import {Permission} from '@sollapay/enums';
import {useListSorting} from '@sollapay/ui';
import {Button, EmptyState, Icon, ListEmptySearch, PageHeader} from '@sollapay/ui/components';
import {ListExportMenu} from '@sollapay/web-core';
import {useCallback, useMemo, useRef, useState} from 'react';
import {Trans, useTranslation} from 'react-i18next';

import {usePermissions} from '@/features/authentication';
import {
  DEFAULT_TEAM_FILTER_STATE,
  TEAM_SORT_CONFIG,
  TeamListSectionHeader,
  TeamMemberTable,
  TeamMemberTableSkeleton,
  TeamPageModals,
  countableTeamMembers,
  createTeamExportColumns,
  useFilteredTeamMembers,
  useTeamMembersQuery
} from '@/features/team';
import {PageContent, PageLayout} from '@/layouts';

import type {TeamFilterState, TeamPageModalsHandle, TeamStatusTab} from '@/features/team';
import type {FirmMemberDTO} from '@sollapay/types';
import type {FC} from 'react';

export const TeamMembersPage: FC = () => {
  const {t, i18n} = useTranslation(['team', 'common']);
  const {hasPermission} = usePermissions();
  const canManageMembers = hasPermission(Permission.TEAM_MANAGE_MEMBERS);
  const canInviteMembers = hasPermission(Permission.TEAM_INVITE_MEMBERS);

  const [searchText, setSearchText] = useState('');
  const [statusTab, setStatusTab] = useState<TeamStatusTab>('all');
  const [filter, setFilter] = useState<TeamFilterState>(DEFAULT_TEAM_FILTER_STATE);
  const modalsRef = useRef<TeamPageModalsHandle | null>(null);

  const {data: members = [], isLoading, error, refetch} = useTeamMembersQuery();
  const countableMembers = useMemo(() => countableTeamMembers(members), [members]);
  const filteredMembers = useFilteredTeamMembers(members, searchText, statusTab, filter);
  const {
    sorting,
    onSortingChange,
    sortedItems: sortedMembers
  } = useListSorting(filteredMembers, TEAM_SORT_CONFIG);

  const exportColumns = useMemo(
    () => createTeamExportColumns(t, i18n.language),
    [t, i18n.language]
  );

  const handleOpenInvite = () => modalsRef.current?.openInvite();
  const handleRemove = useCallback(
    (member: FirmMemberDTO) => modalsRef.current?.openRemove(member),
    []
  );

  const hasActiveFilter =
    filter.statuses.length > 0 || filter.roleKeys.length > 0 || filter.createdAt !== 'all';
  const isFilterOnly = !searchText && (hasActiveFilter || statusTab !== 'all');

  return (
    <PageLayout error={error} refetch={() => void refetch()}>
      <PageHeader
        title={t('title')}
        description={t('subtitle')}
        badge={t('memberCount', {count: countableMembers.length})}
        actions={
          canInviteMembers ? (
            <Button onClick={handleOpenInvite} icon={<Icon name="add" />}>
              {t('invite')}
            </Button>
          ) : undefined
        }
      />
      <PageContent>
        {members.length === 0 && !isLoading ? (
          <div className="flex min-h-150 items-center justify-center">
            <EmptyState
              title={t('noMembersYet')}
              subtitle={t('noMembersSubtitle')}
              action={
                canInviteMembers ? (
                  <Button onClick={handleOpenInvite} icon={<Icon name="add" />}>
                    {t('invite')}
                  </Button>
                ) : undefined
              }
            />
          </div>
        ) : (
          <div data-slot="team-content" className="flex flex-col gap-6">
            <TeamListSectionHeader
              countableMembers={countableMembers}
              searchText={searchText}
              onSearchChange={setSearchText}
              statusTab={statusTab}
              onStatusTabChange={setStatusTab}
              filter={filter}
              onFilterChange={setFilter}
              exportMenu={
                <ListExportMenu<FirmMemberDTO>
                  rows={sortedMembers}
                  columns={exportColumns}
                  refId="firm"
                  entity="team"
                  sheetName={t('title')}
                  disabled={sortedMembers.length === 0}
                />
              }
            />
            {isLoading ? (
              <TeamMemberTableSkeleton />
            ) : sortedMembers.length === 0 ? (
              <ListEmptySearch
                title={t('search.emptyTitle')}
                subtitle={
                  isFilterOnly ? (
                    <Trans t={t} i18nKey="search.emptySubtitleFiltered" components={{br: <br />}} />
                  ) : (
                    <Trans
                      t={t}
                      i18nKey="search.emptySubtitle"
                      values={{term: searchText}}
                      components={{
                        bold: <strong className="font-semibold text-fg-secondary" />,
                        br: <br />
                      }}
                    />
                  )
                }
                clearLabel={isFilterOnly ? t('search.clearFilters') : t('search.clearSearch')}
                onClear={
                  isFilterOnly
                    ? () => {
                        setFilter(DEFAULT_TEAM_FILTER_STATE);
                        setStatusTab('all');
                      }
                    : () => setSearchText('')
                }
              />
            ) : (
              <TeamMemberTable
                rows={sortedMembers}
                sorting={sorting}
                onSortingChange={onSortingChange}
                onRemove={handleRemove}
                canManageMembers={canManageMembers}
              />
            )}
          </div>
        )}
      </PageContent>
      <TeamPageModals ref={modalsRef} onInviteSuccess={() => void refetch()} />
    </PageLayout>
  );
};
